import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import { Close, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import consolidationService from "../../services/consolidationService";
import sailingService from "../../services/sailingService";
import StatusChip from "./StatusChip";
import { selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";

// Shown from both the admin and customer Consolidations pages. Admin gets
// the sailing-assign / schedule-delivery / remove actions; customer only
// gets per-item removal and removing the whole consolidation.
const ConsolidationDetailDialog = ({ open, onClose, consolidationId, isAdmin, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const completedKey = useSelector(selectKeyForRole("consolidation", "completed"));
  const cancelledKey = useSelector(selectKeyForRole("consolidation", "cancelled"));
  const [consolidation, setConsolidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [sailings, setSailings] = useState([]);
  const [selectedSailing, setSelectedSailing] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await consolidationService.getOne(consolidationId);
      setConsolidation(data.data.consolidation);
    } catch {
      enqueueSnackbar("Failed to load consolidation", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && consolidationId) {
      load();
      setSelectedSailing("");
      if (isAdmin) {
        sailingService
          .getAll()
          .then(({ data }) => setSailings(data.data.schedules || []))
          .catch(() => setSailings([]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, consolidationId]);

  const notifyChanged = () => {
    load();
    onChanged?.();
  };

  const handleRemoveItem = async (shipmentId, itemId) => {
    setActing(true);
    try {
      await consolidationService.removeItem(consolidationId, shipmentId, itemId);
      enqueueSnackbar("Item removed", { variant: "success" });
      notifyChanged();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to remove item", { variant: "error" });
    } finally {
      setActing(false);
    }
  };

  const handleRemove = async () => {
    const confirmMessage = isOpenStatus
      ? "Remove this consolidation? Its items and shipments will go back to shipping individually, and this consolidation will be deleted."
      : "Delete this consolidation record? Its items are already done (delivered/cancelled), so this just removes the record itself.";
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setActing(true);
    try {
      await consolidationService.cancel(consolidationId);
      enqueueSnackbar("Consolidation removed", { variant: "success" });
      onChanged?.();
      onClose();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to remove consolidation", { variant: "error" });
    } finally {
      setActing(false);
    }
  };

  const handleAssignSailing = async () => {
    if (!selectedSailing) return;
    setActing(true);
    try {
      await consolidationService.assignSailing(consolidationId, selectedSailing);
      enqueueSnackbar("Consolidation assigned to sailing", { variant: "success" });
      notifyChanged();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to assign sailing", { variant: "error" });
    } finally {
      setActing(false);
    }
  };

  const handlePreferenceChange = async (e) => {
    const holdPreference = e.target.value;
    setActing(true);
    try {
      await consolidationService.updatePreference(consolidationId, holdPreference);
      notifyChanged();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update preference", { variant: "error" });
    } finally {
      setActing(false);
    }
  };

  const handleScheduleDelivery = async () => {
    setActing(true);
    try {
      await consolidationService.scheduleDelivery(consolidationId);
      enqueueSnackbar("Consolidation scheduled for delivery", { variant: "success" });
      notifyChanged();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to schedule delivery", { variant: "error" });
    } finally {
      setActing(false);
    }
  };

  const isOpenStatus =
    !!consolidation && consolidation.status !== completedKey && consolidation.status !== cancelledKey;
  // Admin can remove a consolidation regardless of status — including
  // already-completed/cancelled ones (server's cancelConsolidation has no
  // status restriction). A customer can still only cancel their own while
  // it's genuinely still open.
  const canRemove = !!consolidation && (isAdmin || isOpenStatus);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography fontWeight={700}>{consolidation?.consolidationNumber || "Consolidation"}</Typography>
          {consolidation && (
            <Typography variant="caption" color="text.secondary">
              {consolidation.customer?.firstName} {consolidation.customer?.lastName}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        {loading || !consolidation ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <StatusChip status={consolidation.status} domain="consolidation" />
              <Typography variant="caption" color="text.secondary">
                Requested {dayjs(consolidation.createdAt).format("MMM D, YYYY")}
              </Typography>
            </Box>

            {isOpenStatus && (
              <TextField
                select
                size="small"
                label="What would you like to do?"
                value={consolidation.holdPreference || "ship_when_fulfilled"}
                onChange={handlePreferenceChange}
                disabled={acting}
              >
                <MenuItem value="ship_when_fulfilled">Ship once every item has been received</MenuItem>
                <MenuItem value="hold_for_more">Hold for further shipments</MenuItem>
              </TextField>
            )}

            {isOpenStatus && consolidation.addWindowExpiresAt && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                More items/shipments can be added until{" "}
                <strong>{dayjs(consolidation.addWindowExpiresAt).format("MMM D, YYYY")}</strong> — after that, this
                consolidation is closed to new additions (it can still sail/deliver normally).
              </Alert>
            )}

            {isOpenStatus && consolidation.holdPreference === "hold_for_more" && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {consolidation.holdUntil && (
                  <>
                    If no action is taken by{" "}
                    <strong>{dayjs(consolidation.holdUntil).format("MMM D, YYYY")}</strong>, this consolidation
                    will be automatically sailed.
                  </>
                )}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Shipment</TableCell>
                  {isOpenStatus && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidation.items.map((it) => (
                  <TableRow key={it.itemId}>
                    <TableCell>
                      <Typography variant="body2">{it.description}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {it.trackingNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {it.shipment?.trackingNumber || "—"}
                    </TableCell>
                    {isOpenStatus && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          disabled={acting}
                          onClick={() => handleRemoveItem(it.shipment?._id || it.shipment, it.itemId)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {isAdmin && isOpenStatus && (
              <>
                <Divider>
                  <Typography variant="caption" color="text.secondary">
                    Admin Actions
                  </Typography>
                </Divider>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Every item must be received before this consolidation can sail, and must have cleared customs
                  before it can be scheduled for delivery — all together, not piecemeal.
                </Alert>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    select
                    size="small"
                    label="Sailing"
                    value={selectedSailing}
                    onChange={(e) => setSelectedSailing(e.target.value)}
                    sx={{ flex: 1 }}
                  >
                    {sailings.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.sailingNumber || s.vesselName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" disabled={!selectedSailing || acting} onClick={handleAssignSailing}>
                    Assign Sailing
                  </Button>
                </Box>
                <Button variant="outlined" disabled={acting} onClick={handleScheduleDelivery}>
                  Schedule for Delivery
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {canRemove && (
          <Button color="error" onClick={handleRemove} disabled={acting}>
            {isOpenStatus ? "Remove Consolidation" : "Delete Consolidation"}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsolidationDetailDialog;
